import { DatePicker, Descriptions } from "antd";
import moment from "moment";
import { FC, useEffect, useState } from "react";
import { useTranslation } from 'react-i18next';
import { CgCloseR } from "react-icons/cg";
import { useHistory, useLocation } from "react-router-dom";
import useAuth from "../../../hooks/useAuth";
import useNotification from "../../../hooks/useNotification";
import { formatAmount, getVat, removeComma } from "../../../lib/util";
import { IEvent } from "../../../models/events.model";
import Button from "../../Button";
import CustomTimePicker from "../../CustomTimePicker";
import FileUpload from "../../FileUpload";
import SelectCampusModal from "../../modals/SelectCampusModal";
import Radio from "../../Radio";
import Tinymce from "../../Tinymce";
import MultiCampusModal from "../../statistics/regular-test/modals/MultiCampusModal";
import SelectCampusMultiChoice from "../../SelectCampusMultiChoice";

interface IProps {
  data?: IEvent|undefined;
  submit: (data: FormData) => void;
}

const EducationEditor: FC<IProps> = ({
  data,
  submit,
}) => {
  const { t } = useTranslation();
  const { writeAuthYn } = useAuth();
  const history = useHistory();
  const location = useLocation();
  const { infoNotification } = useNotification();

  const [eventName, setEventName] = useState('');
  const [campuses, setCampuses] = useState<string[]>([]);
  const [eventDate, setEventDate] = useState('');
  const [eventTime, setEventTime] = useState('');
  const [registSrtDate, setRegistSrtDate] = useState('');
  const [registEndDate, setRegistEndDate] = useState('');
  const [paymentAmount, setPaymentAmount] = useState('0');
  const [quota, setQuota] = useState('0');
  const [openYn, setOpenYn] = useState('Y');
  const [content, setContent] = useState('');
  const [files, setFiles] = useState<any>([null]);
  const [multiCampusVisible, setMultiCampusVisible] = useState(false);
  const [isCreate, setIsCreate] = useState(true);
  const [eventFiles, setEventFiles] = useState<any>([]);

  useEffect(() => {
    if (data) {
      setEventName(data.eventName);
      if (data.allCampusYn === 'N') {
        setCampuses(data.campuses.map(item => item.campusCode));
      }
      setEventDate(data.eventDate);
      setEventTime(data.eventTime);
      setRegistSrtDate(data.registSrtDate);
      setRegistEndDate(data.registEndDate);
      setPaymentAmount(String(Number(data.paymentAmount)));
      setQuota(String(Number(data.quota)));
      setOpenYn(data.openYn);
      setContent(data.content);
      setEventFiles(data.eventFiles);
      setIsCreate(false);
    }
  }, [data]);

  const getAmount = (n: string, type: string) => {
    if (!n) return '';

    const amount = parseInt(removeComma(n));
    const vat = getVat(amount);
    return type === 'vat'
      ? formatAmount(String(vat))
      : formatAmount(String(amount - vat));
  };

  const onSetFile = (index: number, file: any) => {
    const _files = [...files];
    _files[index] = file;
    setFiles(_files);
  }

  const onAddFile = () => {
    const _files = [...files];
    _files.push(null);
    setFiles(_files);
  }

  const onDeleteFile = (index: number) => {
    const _files = [...files];
    _files.splice(index, 1);
    setFiles(_files);
  }

  const onSubmit = () => {
    if (!eventName) {
      return infoNotification(t('event.education.message1'));
    }
    if (!eventDate || !eventTime) {
      return infoNotification(t('event.education.message2'));
    }
    if (!registSrtDate) {
      return infoNotification(t('event.education.message3'));
    }
    if (!registEndDate) {
      return infoNotification(t('event.education.message4'));
    }
    if (!paymentAmount) {
      return infoNotification(t('event.education.message5'));
    }
    if (!quota) {
      return infoNotification(t('event.education.message6'));
    }
    if (moment(eventDate).isBefore(registSrtDate) || moment(eventDate).isBefore(registEndDate)) {
      return infoNotification(t('event.education.message7'));
    }
    if (moment(registSrtDate).isAfter(registEndDate)) {
      return infoNotification(t('event.education.message8'));
    }

    const form = new FormData();
    form.append('eventName', eventName);    
    if (campuses.length === 0) {
      form.append('allCampusYn', 'Y');
    } else {
      form.append('allCampusYn', 'N');
      campuses.forEach(item => form.append('campuses', item));
    }
    form.append('eventDate', eventDate);
    form.append('eventTime', eventTime);
    form.append('registSrtDate', registSrtDate);
    form.append('registEndDate', registEndDate);
    form.append('paymentAmount', paymentAmount);
    form.append('taxAmt', getAmount(paymentAmount, 'vat'));
    form.append('quota', quota);
    form.append('openYn', openYn);
    form.append('content', content);

    let isAllDeleted = true;

    if (isCreate) {
      /* 생성 시 파일 처리 */
      if (files.length > 0) {
        files.forEach((item: any) => {
          if (item !== null) {
            form.append('eventFiles', item);
          }
        });
      }
    } else {
      /* 수정 시 파일 처리 */
      if (data?.eventFileCode) {
        form.append('eventFileCode', String(data.eventFileCode));
      }

      if (eventFiles) {
        // 기존 첨부파일 있음
        let hasNewFiles = false;
        files.forEach((item: any) => {
          if (item) hasNewFiles = true;
        });

        let hasDeleted = false;
        eventFiles.forEach((item: any) => {
          if (item.originalName) {
            isAllDeleted = false;
          } else {
            hasDeleted = true;
            form.append('eventDeleteFiles', item.fileSeq);
          }
        });

        if (hasNewFiles) form.append('eventFileStatus', 'update');
        else if (isAllDeleted) form.append('eventFileStatus', 'delete');
        else if (hasDeleted) form.append('eventFileStatus', 'update');
        else form.append('eventFileStatus', 'keep');
      } else {
        // 기존 첨부파일 없음
        if (files.length > 0 && files[0] !== null) {
          // 추가 첨부파일 있음
          form.append('eventFileStatus', 'create');
        } else {
          // 추가 첨부파일 없음
          form.append('eventFileStatus', 'keep');
        }
      }

      if (files.length > 0) {
        files.forEach((item: any) => {
          if (item !== null) {
            form.append('eventNewFiles', item);
          }
        });
      }
    }

    submit(form);
  }

  const onDeleteAttachedFile = (index: number) => {
    const _eventFiles = [...eventFiles];
    _eventFiles[index].originalName = null;
    setEventFiles(_eventFiles);
  }

  return (
    <div className='comm-table__area rowType mt-0'>
      <Descriptions
        bordered
        column={3}
        size="small"
      >
        <Descriptions.Item label={`* ${t('event.education.column10')})`} span={3}>http://localhost:3000/event/education/1036#
          <input
            type="text"
            className="form-control"
            value={eventName}
            onChange={e => setEventName(e.target.value)}
          />          
        </Descriptions.Item>
        <Descriptions.Item label={t('event.education.column11')} span={3}>
          <div>
            {`* ${t('event.education.text6')}`}
          </div>
          <div className="flex">
            <div className="comm-btn__area">
              <SelectCampusMultiChoice
                placeholder={t('statistics.eLearning.title2')}
                onChange={setCampuses}
                value={campuses}
              />
            </div>
            <div className="v-line ml-2">
              {
                campuses?.length > 0
                  ? `(${campuses.length}${t('event.education.text8')})`
                  : `(${t('event.education.text9')})`
              }
            </div>
          </div>
        </Descriptions.Item>
        <Descriptions.Item label={`* ${t('event.education.column12')}`}>
          <div className='comm-gap'>
            <div className="date-picker__area">
              <DatePicker
                value={eventDate ? moment(eventDate) : undefined}
                onChange={e => {
                  if (e === null) setEventDate('');
                  else setEventDate(moment(e).format('YYYYMMDD'))
                }}
              />
            </div>
            <div className="time-picker__area">
              <CustomTimePicker
                value={eventTime}
                onSelect={value => setEventTime(moment(value).format('HH:mm'))}
              />
            </div>
          </div>
        </Descriptions.Item>
        <Descriptions.Item label={`* ${t('event.education.column7')}`}>
          <div className="date-picker__area">
            <DatePicker
              value={registSrtDate ? moment(registSrtDate) : undefined}
              onChange={e => {
                if (e === null) setRegistSrtDate('');
                else setRegistSrtDate(moment(e).format('YYYYMMDD'))
              }}
            />
          </div>
        </Descriptions.Item>
        <Descriptions.Item label={`* ${t('event.education.column8')}`}>
          <div className="date-picker__area">
            <DatePicker
              value={registEndDate ? moment(registEndDate) : undefined}
              onChange={e => {
                if (e === null) setRegistEndDate('');
                else setRegistEndDate(moment(e).format('YYYYMMDD'))
              }}
            />
          </div>
        </Descriptions.Item>
        <Descriptions.Item label={`* ${t('event.education.column4')}`}>
          <div className="flex">
            <input
              type="text"
              className="form-control input-text w-100p"
              value={paymentAmount}
              onChange={e => {
                setPaymentAmount(formatAmount(e.target.value));
              }}
            />
            <span className="v-line ml-1">
              {t('event.education.text3')}{' '}
              {
                paymentAmount
                  ? `(${t('event.education.column13')}: ${getAmount(paymentAmount, 'supplied')}${t('event.education.text3')}, ${t('event.education.column14')}: ${getAmount(paymentAmount, 'vat')}${t('event.education.text3')})`
                  : null
              }
            </span>
          </div>          
        </Descriptions.Item>
        <Descriptions.Item label={`* ${t('event.education.column5')}`}>
          <div className="flex">
            <input
              className="form-control input-text w-100p"
              type="number"
              value={quota}
              onChange={e => setQuota(e.target.value)}
            />
            <span className="v-line ml-1">
              {t('event.education.text5')}{' '}
            </span>
          </div>
        </Descriptions.Item>
        <Descriptions.Item label={t('event.education.column3')}>
          <Radio
            checked={openYn === 'Y'}
            onChange={() => setOpenYn('Y')}
            text={t('common.option.yes')}
          />
          <Radio
            className="ml-4"
            checked={openYn === 'N'}
            onChange={() => setOpenYn('N')}
            text={t('common.option.no')}
          />          
        </Descriptions.Item>
        <Descriptions.Item label={t('event.education.column15')} span={3}>
          <Tinymce
            content={content}
            setContent={setContent}
          />
        </Descriptions.Item>
        {
          eventFiles?.length
            ? (
              <Descriptions.Item label={t('event.education.column16')} span={3}>
                {
                  eventFiles.map((item: any, index: number) => {
                    return item?.originalName
                      ? (
                        <div>
                          <a
                            className="download-link"
                            href={`${item.fileLocation}`}
                            download={item.originalName}
                          >
                            {item.originalName}
                          </a>
                          <span
                            className="ml-1 pointer"
                            onClick={() => onDeleteAttachedFile(index)}
                          >
                            <CgCloseR />
                          </span>
                        </div>
                      )
                      : null;
                  })
                }
              </Descriptions.Item>
            )
            : null
        }        
        <Descriptions.Item label={isCreate ? t('event.education.column17') : t('event.education.column18')} span={4}>
          {
            files.length > 0
              ? files.map((file: any, index: number) => (
                <div className="flex mb-2" key={index}>
                  <FileUpload
                    file={files[index]}
                    setFile={(file: any) => onSetFile(index, file)}
                  />
                  <div className="comm-btn__area">
                    <Button
                      className="btn-basic small gray ml-2"
                      onClick={() => onDeleteFile(index)}
                      text={t('common.button.delete')}
                    />
                  </div>
                </div>
              ))
              : null
          }
          <div className="comm-btn__area">
            <Button
              className="btn-basic pink-line"
              onClick={onAddFile}
              text={t('common.button.add')}
            />
          </div>
        </Descriptions.Item>
      </Descriptions>

      <div className="comm-bx__space">
        <div className="comm-btn__area  right">
          <Button
            onClick={onSubmit}
            className="btn-basic pink"
            text={t('common.button.save')}
            writeAuthYn={writeAuthYn}
          />
          <Button
            onClick={() => history.push(`/event/education${location.search}`)}
            className="btn-basic gray"
            text={t('common.button.cancel')}
          />
        </div>
      </div>

      {multiCampusVisible &&
        <MultiCampusModal
          visible={multiCampusVisible}
          setVisible={setMultiCampusVisible}
          campusCodes={campuses}
          setCampusCodes={setCampuses}
        />
      }
      
    </div>
  );
}

export default EducationEditor;
